import React, { PropsWithChildren } from 'react';
import {
  MasonryEngineFormFieldNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  Button,
  FormField,
  GDSHTMLAutocomplete,
  ParagraphText,
  Popover,
  visuallyHiddenStyle,
} from '@leagueplatform/genesis-core';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';

export type MasonryFormFieldWrapperProps = PropsWithChildren<
  Pick<
    MasonryEngineNodeRendererProps<MasonryEngineFormFieldNode>,
    | 'formFieldProperties'
    | 'rootElementRef'
    | 'className'
    | 'engineClassName'
    | 'locatorId'
    | 'css'
  > & {
    tooltipIcon?: string;
    tooltipContent?: string;
    tooltipLabel?: string;
    hint?: string;
    className?: string;
    autoComplete?: GDSHTMLAutocomplete;
  }
>;

/**
 * Renders a form field within a masonry layout with optional tooltip and status message.
 *
 */
export const MasonryFormFieldWrapper = ({
  formFieldProperties: {
    formFieldId,
    inputStatus,
    statusMessage,
    label,
    hideLabel,
    tooltipIcon,
    tooltipContent,
    tooltipLabel,
    hint,
    showOptionalFlag,
    formFieldType,
  },
  autoComplete,
  rootElementRef,
  children,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryFormFieldWrapperProps) => (
  <FormField
    ref={rootElementRef}
    id={formFieldId}
    label={label}
    name={formFieldId}
    hint={hint}
    hideLabel={hideLabel}
    required={!showOptionalFlag}
    inputStatus={inputStatus}
    statusMessage={statusMessage}
    autoComplete={autoComplete}
    tooltip={
      tooltipContent &&
      tooltipIcon && (
        <Popover.Root>
          <Popover.Trigger>
            <Button
              icon={tooltipIcon}
              quiet
              hideLabel
              priority="secondary"
              size="small"
              css={{ marginInlineStart: '0.5rem' }}
            >
              {tooltipLabel}
            </Button>
          </Popover.Trigger>
          <Popover.Content align="start" side="top" showCloseButton={false}>
            <ParagraphText size="sm">{tooltipContent}</ParagraphText>
          </Popover.Content>
        </Popover.Root>
      )
    }
    className={formatClassNames(
      engineClassName,
      className,
      formFieldType === 'hidden' ? 'visually-hidden' : undefined,
    )}
    css={{
      ...css,
      ...(formFieldType === 'hidden' && visuallyHiddenStyle),
    }}
    data-testid={locatorId}
  >
    {children}
  </FormField>
);
